import { IOrder } from '@wyz/types';
import {
  Badge,
  Button,
  ColumnDefinition,
  Datatable,
  DatatableProps,
  Flex,
  FormatDate,
  FormatDeliveryDate,
  Icon,
  SoftBadge,
  Text,
  Title,
  Tooltip,
} from '@wyz/ui';
import { DateUtils, ORDER_TYPE_CODE, getConditionValue } from '@wyz/utils';
import { sumBy, uniq } from 'lodash-es';
import * as React from 'react';
import { Link } from 'react-router';
import { useManageEnums } from '~/common/api';
import { PrivateComponent } from '~/common/components';
import { Trans, useI18n, useLocale } from '~/common/context';
import { ROLES } from '~/common/helpers';
import { useAccessControl, useListLocationState } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import { PurchaseQueryKeys, useManageOrders } from '~/purchase/api';
import {
  CollapsibleList,
  OrdersHistoryTrackingListModal,
  SeasonIcon,
} from '~/purchase/components';
import { OrderHelpers } from '~/purchase/helpers';
import { ORDER_STATUS_LABEL_LOOKUP } from '~/purchase/translators';
import { IOrderFilterQuery } from '~/purchase/types';
// Above this row count the "export all" download is disabled; the user must narrow their search.
const ORDER_EXPORT_MAX_ROWS = 1000;
const ORDER_COLUMNS = [
  'reference',
  'status',
  'orderDate',
  'tyreLines',
  'supplier',
  'deliveryDate',
  'tracking',
  'comment',
  'ordererName',
  'type',
] as const;
type OrderColumn = (typeof ORDER_COLUMNS)[number];
type Props = {
  fetcher: DatatableProps<IOrder, IOrderFilterQuery>['fetcher'];
  onExport?: (criteria: IOrderFilterQuery) => void;
  onPrint: (orderReference: string) => void;
  customerTimezone?: string;
  customer: IUserCustomer;
  excludedColumns?: Array<OrderColumn>;
};
function OrdersHistoryTable({
  fetcher,
  children,
  onExport,
  onPrint,
  customer,
  customerTimezone,
  excludedColumns = [],
}: React.PropsWithChildren<Props>) {
  const listState = useListLocationState();
  const { t } = useI18n();
  const acl = useAccessControl();
  const { locale } = useLocale();

  const [openedTracking, setOpenedTracking] = React.useState<
    string | undefined
  >(undefined);
  const manageEnums = useManageEnums({
    getOrderTypes: { enabled: true },
  });
  const manageOrders = useManageOrders({
    getDistinctSuppliers: {
      enabled: true,
      args: { customerId: customer.id, includeCustomerChildren: false },
    },
    getDistinctStatuses: {
      enabled: true,
      args: { customerId: customer.id },
    },
  });
  const supplierFilterOptions = OrderHelpers.flattenSuppliers(
    manageOrders.getDistinctSuppliers.data?.data ?? [],
  );
  const getProductLines = (order: IOrder) => [
    ...order.tyreLines,
    ...(order.wheelTyreLines ?? []),
  ];
  const columns: Array<ColumnDefinition<IOrder, OrderColumn>> = [
    {
      field: 'reference',
      headerName: <Trans i18nKey='app.purchase.orders.table.reference' />,
      width: '10%',
      renderCell: ({ row }) => (
        <>
          <Text color='tertiary' weight='bold' heading='sm'>
            {row.orderReference}
          </Text>
          {!row.tyreless && (
            <div>
              <SoftBadge pill bg='secondary' className='fs-xxs'>
                <Trans
                  i18nKey='app.purchase.cart_count'
                  values={{
                    referenceCount: uniq(
                      getProductLines(row).map((item) => item.ean),
                    ).length,
                    productCount: sumBy(getProductLines(row), 'quantity'),
                  }}
                />
              </SoftBadge>
            </div>
          )}
          <Flex
            justifyContent='start'
            alignItems='center'
            className='space-x-1'
          >
            <PrivateComponent roles={[ROLES.ROLE_GET_ORDERS]}>
              <Button
                id={`${row.orderReference}-detail`}
                variant='link'
                size='xs'
                as={Link}
                to={`${row.orderReference}`}
                state={listState}
              >
                <Text
                  component={Flex}
                  heading='xs'
                  weight='semi-bold'
                  color='tertiary'
                  alignItems='center'
                  className='space-x-1'
                >
                  <Icon icon='eye' size='xs' />
                </Text>
              </Button>
            </PrivateComponent>
            {row.printable && (
              <PrivateComponent roles={[ROLES.ROLE_PRINT_ORDERS]}>
                <Button
                  id={`${row.orderReference}-print`}
                  variant='link'
                  size='xs'
                  onClick={() => onPrint(row.orderReference)}
                >
                  <Text
                    component={Flex}
                    heading='sm'
                    weight='semi-bold'
                    color='tertiary'
                    alignItems='center'
                    className='space-x-1'
                  >
                    <Icon icon='print' size='sm' />
                  </Text>
                </Button>
              </PrivateComponent>
            )}
          </Flex>
        </>
      ),
    },
    {
      field: 'status',
      headerName: <Trans i18nKey='app.common.status' />,
      filterable: true,
      width: '7%',
      filter: {
        rank: 1,
        type: 'LIST',
        options: manageOrders.getDistinctStatuses.data || [],
        isMulti: true,
        isLoading: manageOrders.getDistinctStatuses.isProcessing,
        operator: 'eq',
      },
      renderCell: ({ row }) => (
        <Text heading='xs'>
          <Badge
            bg={OrderHelpers.ORDER_STATUS_COLOR_LOOKUP[row.logisticStatusId]}
            pill
          >
            <Trans i18nKey={ORDER_STATUS_LABEL_LOOKUP[row.logisticStatusId]} />
          </Badge>
        </Text>
      ),
      filterColSpan: {
        xxl: 1,
        xl: 2,
        lg: 4,
      },
    },
    {
      field: 'orderDate',
      headerName: <Trans i18nKey='app.purchase.orders.table.order_date' />,
      width: '8%',
      filterable: true,
      filter: {
        rank: 3,
        type: 'DATE_RANGE',
        operator: 'eq',
        placeholder: t('app.purchase.orders.filter.order_date.placeholder'),
        translations: {
          applyButton: t('app.common.button.apply'),
          resetButton: t('app.common.button.reset'),
        },
        locale,
      },
      renderCell: ({ row }) => (
        <Text weight='semi-bold' heading='xs'>
          <FormatDate
            date={row.orderedAt}
            format={DateUtils.DATE_FORMAT.DMY_HM}
            timezone={customerTimezone}
          />
        </Text>
      ),
    },
    {
      field: 'tyreLines',
      headerName: <Trans i18nKey='app.purchase.orders.table.items' />,
      align: 'left',
      width: '26%',
      renderCell: ({ row }) =>
        row.type === ORDER_TYPE_CODE.REFUSAL_TAKE_BACK ? (
          <CollapsibleList
            limit={4}
            items={row.deliveryLines ?? []}
            renderItem={(item, index) => (
              <li key={index}>
                <Flex className='space-x-1' alignItems='center'>
                  <Text weight='semi-bold' heading='xs' color='secondary'>
                    {item.quantity}
                    {'x'} {item.description}
                  </Text>
                </Flex>
              </li>
            )}
          />
        ) : (
          <CollapsibleList
            limit={4}
            items={getProductLines(row)}
            renderItem={(item, index) => {
              if (item.wheel) {
                return (
                  <li key={index}>
                    <Flex className='space-x-1' alignItems='center'>
                      <Text weight='semi-bold' heading='xs' color='secondary'>
                        {item.quantity}
                        {'x'}{' '}
                        {t('app.purchase.wcw.detail.set_of', {
                          count: item.wheel.set,
                        })}{' '}
                        {item.wheel.name}
                      </Text>
                      <SeasonIcon value={item.season} size='xs' />
                    </Flex>
                    <Flex className='space-x-1' alignItems='center'>
                      <Text weight='normal' heading='xs' color='secondary-gray'>
                        {item.dimension}
                      </Text>
                      <Text weight='normal' heading='xs' color='secondary-gray'>
                        {t('app.common.product.reference_value', {
                          value: item.wheel.reference,
                        })}
                      </Text>
                    </Flex>
                  </li>
                );
              }

              const identifiers = OrderHelpers.getTyreLineIdentifier(
                customer.settingTemplate?.generalSetting.tyreIdentifierFields,
                item,
              );
              return (
                <li key={index}>
                  <Flex className='space-x-1' alignItems='center'>
                    <Text weight='semi-bold' heading='xs' color='secondary'>
                      {item.quantity}
                      {'x'} {item.brand} {item.profile}
                    </Text>
                    <SeasonIcon value={item.season} size='xs' />
                  </Flex>
                  <Flex className='space-x-1' alignItems='center'>
                    <Text weight='normal' heading='xs' color='secondary-gray'>
                      {item.dimension}
                    </Text>
                    <Text weight='normal' heading='xs' color='secondary-gray'>
                      {t('app.common.product.ean_value', {
                        value: item.ean,
                      })}
                    </Text>
                    {identifiers.map((identifier) => (
                      <Text
                        key={`${identifier.translationKey}-${identifier.reference}`}
                        weight='normal'
                        heading='xs'
                        color='secondary-gray'
                      >
                        {t(identifier.translationKey, {
                          value: identifier.reference,
                        })}
                      </Text>
                    ))}
                  </Flex>
                </li>
              );
            }}
          />
        ),
    },
    {
      field: 'supplier',
      headerName: <Trans i18nKey='app.purchase.orders.table.supplier' />,
      width: '8%',
      ...(acl.isGranted([ROLES.ROLE_CREATE_CALL_CENTER_ORDER])
        ? {
            filterable: true,
            filter: {
              rank: 2,
              type: 'LIST',
              options: supplierFilterOptions,
              isLoading: manageOrders.getDistinctSuppliers.isProcessing,
              isMulti: true,
              operator: 'eq',
            },
          }
        : {
            filterable: undefined,
          }),

      renderCell: ({ row }) => (
        <>
          <Text
            color='tertiary'
            weight='semi-bold'
            heading='xs'
            className='text-wrap'
          >
            {row.supplierName}
          </Text>
          {row.originOrderReference && row.originChildCustomerId && (
            <Link
              key={row.originOrderReference}
              to={`/purchase/orders/processed/customers/${row.originChildCustomerId}/${row.originOrderReference}`}
            >
              <Text weight='semi-bold' heading='xs' color='tertiary'>
                {row.originOrderReference}
              </Text>
            </Link>
          )}
        </>
      ),
    },
    {
      field: 'deliveryDate',
      headerName: <Trans i18nKey='app.common.orders.delivery_date' />,
      width: '5%',
      filterable: true,
      filter: {
        rank: 4,
        type: 'DATE_RANGE',
        operator: 'eq',
        placeholder: t('app.purchase.orders.filter.delivery_date.placeholder'),
        translations: {
          applyButton: t('app.common.button.apply'),
          resetButton: t('app.common.button.reset'),
        },
        locale,
      },
      renderCell: ({ row }) => (
        <Flex direction='column' alignItems='center'>
          {row.todayDelivery && <Icon icon='today-delivery-wording' />}
          {row.morningDelivery && <Icon icon='morning-delivery-wording' />}
          <Text weight='semi-bold' heading='xs'>
            <FormatDeliveryDate
              orderType={row.type}
              backorderContent={
                <Trans i18nKey='app.common.offer_type.backorder' />
              }
              date={row.dateDelivered}
              format={DateUtils.DATE_FORMAT.DMY}
              timezone={customerTimezone}
            />
          </Text>
        </Flex>
      ),
    },
    {
      field: 'tracking',
      headerName: <Trans i18nKey='app.purchase.orders.table.tracking' />,
      width: '7%',
      renderCell: ({ row }) => (
        <Flex justifyContent='center'>
          {row.trackings && row.trackings.length > 0 ? (
            <>
              <OrdersHistoryTrackingListModal
                onClose={() => setOpenedTracking(undefined)}
                open={openedTracking === row.orderReference}
                order={row}
              />
              <Button
                id={`${row.orderReference}-open-tracking`}
                variant='tertiary-blue'
                size='xs'
                onClick={() =>
                  row.trackings!.length === 1
                    ? window.open(row.trackings!.at(0)!.url, '_blank')
                    : setOpenedTracking(row.orderReference)
                }
              >
                <Icon icon='box-line' size='xs' className='me-1' />
                <Trans
                  i18nKey='app.purchase.order_detail.tracking.n_package'
                  values={{
                    count: row.trackings.length,
                  }}
                />
              </Button>
            </>
          ) : (
            '-'
          )}
        </Flex>
      ),
    },
    {
      field: 'comment',
      headerName: <Trans i18nKey='app.purchase.orders.table.comment' />,
      width: '5%',
      renderCell: ({ row }) => (
        <>
          {row.comment ? (
            <Tooltip label={<>{row.comment}</>}>
              <span>
                <Icon icon='info-circle' size='xs' />
              </span>
            </Tooltip>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      field: 'ordererName',
      headerName: <Trans i18nKey='app.common.user' />,
      width: '5%',
      renderCell: ({ row }) => <strong>{row.ordererName || '-'}</strong>,
    },
    {
      field: 'type',
      headerName: <Trans i18nKey='app.purchase.orders.table.type' />,
      width: '5%',
      renderCell: ({ row }) => (
        <div>
          <Tooltip
            label={
              row.type ? manageEnums.getOrderTypes.get(row.type)?.label : ''
            }
            enabled={true}
          >
            <Badge pill bg='blue'>
              <Text
                heading='xxs'
                weight='semi-bold'
                color='primary'
                component='span'
              >
                {row.type || '-'}
              </Text>
            </Badge>
          </Tooltip>
        </div>
      ),
    },
  ];
  return (
    <Datatable.Container<IOrder, IOrderFilterQuery>
      qsAware
      columns={
        excludedColumns.length === 0
          ? columns
          : columns.filter((item) => !excludedColumns.includes(item.field))
      }
      defaults={{
        limit: 50,
      }}
      queryKey={PurchaseQueryKeys.orders.base(customer.id)}
      translations={{
        searchLabel: (
          <Trans i18nKey='app.common.datatable.filter.query.label' />
        ),
        searchPlaceholder: t(
          'app.purchase.orders.filter.reference.placeholder',
        ),
      }}
      queryTransformer={(query) => {
        const selectedSuppliersIds: Array<number> =
          getConditionValue(query.conditions, 'supplier')?.value ?? [];

        const targetIds = selectedSuppliersIds
          .flatMap(
            (item) =>
              supplierFilterOptions.find((option) => option.value === item)
                ?.targetIds,
          )
          .filter(Boolean) as number[];

        return {
          query: query.query,
          supplierIds: targetIds,
          status: getConditionValue(query.conditions, 'status')?.value,
          orderFromDate: getConditionValue(query.conditions, 'orderDate')
            ?.value[0],
          orderToDate: getConditionValue(query.conditions, 'orderDate')
            ?.value[1],
          deliveryFromDate: getConditionValue(query.conditions, 'deliveryDate')
            ?.value[0],
          deliveryToDate: getConditionValue(query.conditions, 'deliveryDate')
            ?.value[1],
        };
      }}
      fetcher={fetcher}
    >
      {({ data }) => (
        <>
          <div>
            <Title heading='xl' weight='bold'>
              <Trans i18nKey='app.purchase.orders.title' />
            </Title>
            <Datatable.Filter<IOrder> />
          </div>
          <Datatable.Header<IOrder, IOrderFilterQuery>
            onExport={onExport}
            exportDisabled={(data?.meta.total ?? 0) > ORDER_EXPORT_MAX_ROWS}
            exportInfoTooltip={
              <Trans
                i18nKey='app.purchase.orders.export.info_tooltip'
                value={ORDER_EXPORT_MAX_ROWS}
              />
            }
            exportDisabledTooltip={
              <Trans i18nKey='app.purchase.orders.export.disabled_tooltip' />
            }
          />
          <Datatable.Background stickyHeader>
            {children}
            <Datatable.Table stickyHeader />
            <hr />
            <Datatable.Footer />
          </Datatable.Background>
        </>
      )}
    </Datatable.Container>
  );
}

export default OrdersHistoryTable;
